<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12">
        <top-nav :page_title="page_title"></top-nav>

        <!-- FLASH MESSAGES -->
        <FlashMessage :position="'right bottom'"></FlashMessage>
        <br> <br>
        <table class=" table table-hover">
          <thead>
          <tr>
            <th class="table-header-style" scope="col"> Id</th>
            <th class="table-header-style" scope="col"> Name</th>
            <th class="table-header-style" scope="col"> Theme</th>
            <th class="table-header-style" scope="col"> Project Head</th>
            <th class="table-header-style" scope="col"> Code</th>
            <th class="table-header-style" scope="col"> Description</th>
            <th v-if="isResearcher() || isThemeAdmin() || isAdmin()" class="table-header-style" scope="col"> Actions</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(project, index) in response" :key="project.id">
            <td> {{ index + 1 }}</td>
            <td> {{ project.name }}</td>
            <td> {{ project['theme.name'] }}</td>
            <td>
              {{ project['lead.first_name'] }} {{ project['lead.last_name'] }}
              <a :href="`mailto:${project['lead.email']}`" target="_blank">
                <b-icon
                    v-b-tooltip.hover
                    :font-scale="`${font_scale}`"
                    :title="`Contact project head (${ project['lead.email'] })`" class="border border-info rounded"
                    icon="envelope" variant="info"
                ></b-icon>
              </a>
            </td>
            <td> {{ project.code }}</td>
            <td> {{ project.description }}</td>

            <td v-if="isProjectOwner(project['lead.email'])">
              <b-icon
                  v-b-modal.modal-project-edit
                  v-b-tooltip.hover
                  :font-scale="`${font_scale}`" :title="`Update project ${ project.name }`"
                  class="border border-info rounded" icon="pencil"
                  variant="info"
                  @mouseover="fillFormForUpdate(project.code, project.name, project.description, project['theme.name'],
                                    project['lead.first_name'] + ' ' + project['lead.last_name'])"
              ></b-icon>
              &nbsp;
              <b-icon
                  v-b-tooltip.hover :font-scale="`${font_scale}`"
                  :title="`Delete project ${project.name}!`" class="border rounded bg-danger p-1"
                  icon="trash" variant="light"
                  @click="deleteProject(project.code)"
              ></b-icon>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!isEditing">
        <b-modal
            id="modal-project"
            cancel-variant="danger"
            ok-title="Save"
            title="Add Project"
            @hidden="clearForm"
            @ok="onSubmit"
            @submit="clearForm"
        >
          <form @submit.prevent="createProject">

            <!-- PROJECT THEME -->
            <b-form-group id="form-theme-group" label="Theme:" label-for="form-theme-input">
              <ejs-dropdownlist
                  id='dropdownlist'
                  :dataSource='themeDataList'
                  :fields="themeDataFields"
                  :v-model="project.theme"
                  placeholder='Select a theme'
              ></ejs-dropdownlist>
              <div v-if="$v.project.theme.$dirty">
                <div v-if="!$v.project.theme.required" class="error">Field is required</div>
              </div>
            </b-form-group>

            <!-- PROJECT HEAD -->
            <b-form-group id="form-head-group" label="Project Head:" label-for="form-head-input"
                          v-if="project_head_available">
              <ejs-dropdownlist
                  id='dropdownlist-head'
                  :dataSource='headDataList'
                  :fields="headDataFields"
                  :v-model="project.head"
                  placeholder='Select the project head'
              ></ejs-dropdownlist>
              <div v-if="$v.project.head.$dirty">
                <div v-if="!$v.project.head.required" class="error">Field is required</div>
              </div>
            </b-form-group>

            <!-- ICON to add project head details if they are not found in the dropdown above -->
            <div style="float: right" class="menu_icon" title="Can't find project head?">
              <i v-if="project_head_available" class="fas fa-plus-circle" @click="enable_hidden_div"></i>
              <i v-if="!project_head_available" class="fas fa-minus-circle" @click="enable_hidden_div"></i>
            </div>

            <!-- SECTION ONLY TO BE SHOWN IF project_head_available is FALSE -->
            <div v-if="!project_head_available" class="project-div-format">
              <!-- PROJECT HEAD NAME -->
              <b-form-group :class="{ 'form-group--error': $v.project.head_name.$error }"
                  id="form-head-group" label="Name of Project Head:" label-for="form-head-input">
                <b-form-input
                    id="form-head-input"
                    v-model="project.head_name"
                    placeholder="Enter project head name"
                    required
                    type="text"
                ></b-form-input>
                <div v-if="$v.project.head_name.$dirty">
                  <div v-if="!$v.project.head_name.required" class="error">Field is required</div>
                  <div v-if="!$v.project.head_name.fullName" class="error">Enter at least 2 names separated by space</div>
                </div>
              </b-form-group>

              <!-- PROJECT HEAD EMAIL -->
              <b-form-group :class="{ 'form-group--error': $v.project.head_email.$error }"
                  id="form-email-group" label="Email of Project Head:" label-for="form-email-input">
                <b-form-input
                    id="form-email-input"
                    v-model="project.head_email"
                    placeholder="Enter project's head email"
                    required
                    type="text">
                </b-form-input>
                <div v-if="$v.project.head_email.$dirty">
                  <div v-if="!$v.project.head_email.required" class="error">Field is required</div>
                  <div v-if="!$v.project.head_email.email" class="error">Email format not correct</div>
                </div>
              </b-form-group>
            </div>
            <!-- END OF ADDITIONAL SECTION -->

            <!-- PROJECT CODE -->
            <b-form-group :class="{ 'form-group--error': $v.project.code.$error }"
                id="form-code-group" label="Code:" label-for="form-code-input">
              <b-form-input
                  id="form-code-input"
                  v-model="project.code"
                  placeholder="Enter Code"
                  required
                  type="text"
              ></b-form-input>
              <div v-if="$v.project.code.$dirty">
                <div v-if="!$v.project.code.required" class="error">Field is required</div>
              </div>
            </b-form-group>

            <!-- PROJECT NAME -->
            <b-form-group :class="{ 'form-group--error': $v.project.name.$error }"
                id="form-name-group" label="Name:" label-for="form-name-input">
              <b-form-input
                  id="form-name-input"
                  v-model="project.name"
                  placeholder="Enter Project Name"
                  required
                  type="text">
              </b-form-input>
              <div v-if="$v.project.name.$dirty">
                <div v-if="!$v.project.name.required" class="error">Field is required</div>
              </div>
            </b-form-group>

            <!-- PROJECT DESCRIPTION -->
            <b-form-group :class="{ 'form-group--error': $v.project.description.$error }"
                id="form-desc-group" label="Description:" label-for="form-desc-input">
              <b-form-textarea
                  id="form-desc-input"
                  v-model="project.description"
                  placeholder="Enter Description"
                  required
                  type="text">
              </b-form-textarea>
              <div v-if="$v.project.description.$dirty">
                <div v-if="!$v.project.description.required" class="error">Field is required</div>
              </div>
            </b-form-group>
          </form>
        </b-modal>
      </div>

      <div v-else-if="isEditing">
        <b-modal
            id="modal-project-edit"
            cancel-variant="danger"
            ok-title="Update"
            title="Edit Project"
            @hidden="clearForm"
            @ok="updateProject(old_code)"
            @shown="selectDropDownItemsForUpdate(project.theme, project.head)"
            @submit="showModal = false"
        >
          <form @submit.prevent="updateProject">
            <b-form-group id="form-theme-group-edit" label="Theme:" label-for="form-theme-input">
              <ejs-dropdownlist
                  id='dropdownlist'
                  :dataSource='themeDataList'
                  :fields="themeDataFields"
                  :v-model="project.theme"
                  placeholder='Select a theme'
              ></ejs-dropdownlist>
            </b-form-group>

            <b-form-group id="form-head-group-edit" label="Project Head:" label-for="form-head-input">
              <ejs-dropdownlist
                  id='dropdownlist-head'
                  :dataSource='headDataList'
                  :fields="headDataFields"
                  :v-model="project.head"
                  placeholder='Select the project head'
              ></ejs-dropdownlist>
            </b-form-group>

            <b-form-group id="form-code-group-edit" label="Code:" label-for="form-code-input">
              <b-form-input
                  id="form-code-input"
                  v-model="project.code"
                  placeholder="Enter Code"
                  required
                  type="text"
              ></b-form-input>
            </b-form-group>

            <b-form-group id="form-name-group-edit" label="Name:" label-for="form-name-input">
              <b-form-input
                  id="form-name-input"
                  v-model="project.name"
                  placeholder="Enter Project Name"
                  required
                  type="text">
              </b-form-input>
            </b-form-group>

            <b-form-group id="form-desc-group-edit" label="Description:" label-for="form-desc-input">
              <b-form-textarea
                  id="form-desc-input"
                  v-model="project.description"
                  placeholder="Enter Description"
                  required
                  type="text">
              </b-form-textarea>
            </b-form-group>
          </form>
        </b-modal>
      </div>

      <b-button v-if="isLabAdmin() || isResearcher() || isThemeAdmin() || isAdmin()"
                v-b-modal.modal-project class="float_btn" style="border-radius: 50%"
                variant="primary">
        <span>Add Project</span> <i class="fas fa-plus-circle menu_icon"></i>
      </b-button>

      <div style="margin: auto;">
        <loading-progress :hide-background="hideBackground" :indeterminate="indeterminate" :progress="progressPath"
                          :size="size" fillDuration="2" rotate rotationDuration="1"/>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getSelectedItem, getSelectedItemCode, getProjectDataList, getThemeDataList, getUserDataList,
  handleError, isAdmin, isLabAdmin, isResourceOwner, isResearcher, isThemeAdmin,
  pageStartLoader, secureStoreGetAuthString, showFlashMessage
} from "@/utils/util_functions";
import {font_scale} from '@/utils/constants';
import {project_resource} from "@/utils/api_paths";
import axios from "axios";
import TopNav from "@/components/TopNav";
import {required, email} from "vuelidate/lib/validators";
import EventBus from "@/components/EventBus";

export default {
  name: "Project",
  data() {
    return {
      page_title: "Project",
      response: [],
      project: {
        code: null,
        name: null,
        description: null,
        theme: null,
        head: null,
        head_name: null,
        head_email: null
      },

      project_head_available: true,

      font_scale,

      // loader-time
      time: 2000,

      // progressPath
      indeterminate: true,
      hideBackground: true,
      progressPath: 5,
      size: 180,

      themeDataList: [], themeDataFields: {text: '', value: ''},
      headDataList: [], headDataFields: {text: '', value: ''},

      // values for data modification
      old_code: null,
      showModal: true,
      isEditing: false,

      data: {}
    };
  },

  validations: {
     project: {
        code: {required},
        name: {required},
        description: {required},
        theme: {required},
        head: {required},
        head_email: {required, email},
        head_name: {required,
          fullName(full_name) {
          return (
              /[A-Za-z]/.test(full_name) && //checks for a-z
              /[ ]/.test(full_name) && //checks for space
              /[A-Za-z]/.test(full_name) && //checks for a-z
              full_name.length >= 6
          );
        }},
    },
  },

  mounted() {
    // get and set events of project data from drop-down
    EventBus.$on('get-project-data-list', payload => {
      this.response = payload['projectList']
      this.haltProgressPath()
    })

    // get theme data list
    EventBus.$on('get-theme-data-list', payload => {
      this.themeDataList = payload['selectorList']['items']
      this.themeDataFields = payload['selectorList']['fields']
    })

    // get user data list
    EventBus.$on('get-user-data-list', payload => {
      this.headDataList = payload['items']
      this.headDataFields = payload['fields']
    })
  },

  methods: {
    // users allowed to modify this resource
    isLabAdmin, isResearcher, isThemeAdmin, isAdmin, isProjectOwner: isResourceOwner,

    // Util Functions
    onSubmit(evt) {
      this.$v.$touch();
      this.project.theme = getSelectedItem(this.themeDataList, this.project.theme);
      this.project.head = getSelectedItemCode('dropdownlist-head', this.headDataList);

      this.data = {
        code: this.project.code,
        name: this.project.name,
        theme: this.project.theme,
        description: this.project.description,
      }

      if (this.$v.$invalid) {
        if (this.project.head === null && this.project.head_name === null && this.project.head_email === null){
         // stop here if form is invalid
          evt.preventDefault()
          return;
        }
        // Project head has been selected from drop-down-list normally
        if (typeof this.project.head !== 'undefined' && this.project.head !== null){
          // update the data object to be sent to backend
          this.data['head'] = this.project.head
        }
        // Project head details have been passed since they are not found.
        else if (!this.$v.project.head_name.$invalid || !this.$v.project.head_email.$invalid){
          // update the data object to be sent to backend
          this.data['head_name'] = this.project.head_name
          this.data['head_email'] = this.project.head_email
        } else {
           // stop here if form is invalid
          evt.preventDefault()
          return;
        }
      }
      this.createProject();
    },

    enable_hidden_div(){this.project_head_available = !this.project_head_available},
    
    clearForm() {
      this.project.code = null;
      this.project.name = null;
      this.project.theme = null;
      this.project.head = null;
      this.project.description = null;
      this.isEditing = false;
      this.themeDataList = [];
      this.headDataList = [];
      this.project_head_available = true;
      this.project.head_email = null;
      this.project.head_name = null;
      this.$v.$reset();
    },

    fillFormForUpdate(code, name, description, theme, head) {
      this.project.code = code;
      this.project.name = name;
      this.project.description = description;
      this.project.theme = theme;
      this.project.head = head;
      this.old_code = code;
      this.isEditing = true;
      this.showModal = true;
    },

    selectDropDownItemsForUpdate(theme, head) {
      let elementTheme = document.getElementById("dropdownlist");
      elementTheme.value = theme;

      let elementHead = document.getElementById("dropdownlist-head");
      elementHead.value = head;
    },

    // stop&hide progressPath
    haltProgressPath(cont = false, path = 0, size = 0) {
      this.indeterminate = cont
      this.progressPath = path
      this.size = size
    },

    /** Functions to interact with api below.
     * NB: The get function has been replaced by getProjectDataList(); which is a util function,
     * which is called on create and the emitted event i.e. get-theme-data-list is listened to in mounted(). */

    createProject: function () {
      let loader = pageStartLoader(this)

      axios.post(project_resource, this.data, {
        headers:
            {
              Authorization: secureStoreGetAuthString()
            }
      }).then((response) => {
        setTimeout(() => {
          loader.hide()
          getProjectDataList();
          showFlashMessage(this, 'success', response.data['message'], '');
        }, this.time)
      }).catch((error) => {
        handleError(this, error, loader)
      });
    },

    updateProject: function (code) {
      this.project.theme = getSelectedItem(this.themeDataList, this.project.theme);
      this.project.head = getSelectedItemCode('dropdownlist-head', this.headDataList);
      let loader = pageStartLoader(this)

      axios.put(project_resource, {
        code: this.project.code,
        name: this.project.name,
        head: this.project.head,
        theme: this.project.theme,
        description: this.project.description,
      }, {
        headers:
            {
              code: code,
              Authorization: secureStoreGetAuthString()
            }
      }).then((response) => {
        setTimeout(() => {
          loader.hide()
          getProjectDataList();
          showFlashMessage(this, 'success', response.data['message'], '');
          this.clearForm();
        }, this.time)
      }).catch((error) => {
        handleError(this, error, loader)
      });
    },

    deleteProject: function (code) {
      let loader = pageStartLoader(this)
      axios.delete(project_resource, {
        headers:
            {
              code: code,
              Authorization: secureStoreGetAuthString()
            }
      }).then((response) => {
        setTimeout(() => {
          loader.hide()
          getProjectDataList();
          showFlashMessage(this, 'success', response.data['message'], '');
        }, this.time)
      }).catch((error) => {
        handleError(this, error, loader)
      });
    },
  },
  created() { getProjectDataList(); getThemeDataList(); getUserDataList(); },
  components: {TopNav}
}
</script>
